import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';

interface ElementStyle {
  fontSize?: number;
  align?: 'left' | 'center' | 'right';
  fontWeight?: string;
  fontStyle?: string;
  color?: string;
}

interface CredentialElement {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  field?: string;
  content?: string;
  style?: ElementStyle;
}

interface CredentialLayout {
  canvas: {
    width: number;
    height: number;
    unit: 'mm';
    background: { color: string; media_id: string | null };
  };
  elements: CredentialElement[];
  signatures: unknown[];
}

interface TemplateEditor {
  id: string;
  name: string;
  publishedAt: string | null;
  layout: CredentialLayout;
}

interface MediaHit {
  id: string;
  title: string;
  mimeType: string;
}

const FIELD_BINDINGS = [
  { value: 'recipient_name', label: 'Nombre del estudiante' },
  { value: 'course_name', label: 'Curso' },
  { value: 'learning_path_name', label: 'Ruta' },
  { value: 'program_name', label: 'Programa' },
  { value: 'instructor_name', label: 'Instructor' },
  { value: 'issued_date', label: 'Fecha de emisión' },
  { value: 'hours', label: 'Horas' },
  { value: 'grade', label: 'Calificación' },
  { value: 'verification_code', label: 'Código de verificación' },
  { value: 'title', label: 'Título de credencial' },
] as const;

interface UserCustomFieldOption {
  name: string;
  slug: string;
}

const INPUT =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-2 px-3 text-sm text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function mmScale(layout: CredentialLayout, zoom = 1) {
  return Math.min(520 / layout.canvas.width, 380 / layout.canvas.height) * zoom;
}

function layerName(el: CredentialElement, labels: Map<string, string>) {
  if (el.type === 'field') return labels.get(el.field || '') || el.field || 'Campo';
  if (el.type === 'qr') return 'Código QR';
  if (el.type === 'text') return el.content || 'Texto';
  return el.type;
}

function layerIcon(el: CredentialElement) {
  if (el.type === 'qr') return 'qr_code_2';
  if (el.type === 'text') return 'text_fields';
  const icons: Record<string, string> = {
    title: 'title',
    recipient_name: 'person',
    course_name: 'school',
    issued_date: 'event',
    verification_code: 'vpn_key',
    hours: 'schedule',
    grade: 'grade',
    instructor_name: 'person',
    program_name: 'account_tree',
    learning_path_name: 'route',
  };
  return icons[el.field || ''] || 'label';
}

function typeBadge(type: string) {
  if (type === 'qr') return 'QR';
  if (type === 'text') return 'Texto';
  return 'Campo';
}

function elementStyle(el: CredentialElement, scale: number): CSSProperties {
  const st = el.style ?? {};
  const align = st.align || 'left';
  return {
    left: el.x * scale,
    top: el.y * scale,
    width: el.width * scale,
    height: el.height * scale,
    fontSize: (st.fontSize || 12) * (scale / 2.116),
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
    textAlign: align,
    fontWeight: (st.fontWeight || (el.field === 'recipient_name' ? 'bold' : 'normal')) as CSSProperties['fontWeight'],
    fontStyle: (st.fontStyle || (el.field === 'course_name' ? 'italic' : 'normal')) as CSSProperties['fontStyle'],
    color: st.color || (el.field === 'recipient_name' ? '#142175' : '#1a1c1e'),
  };
}

function displayLabel(el: CredentialElement, labels: Map<string, string>) {
  if (el.type === 'field') return `[${labels.get(el.field || '') || el.field || 'campo'}]`;
  if (el.type === 'text') return el.content || 'Texto';
  return '';
}

export function CredentialTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [layout, setLayout] = useState<CredentialLayout | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerNonce, setPickerNonce] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerResults, setPickerResults] = useState<MediaHit[]>([]);
  const dragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number; scale: number } | null>(null);

  const query = useQuery({
    queryKey: ['credentials', 'templates', id],
    queryFn: () => api<TemplateEditor>(`/credentials/templates/${id}`),
    enabled: Boolean(id),
  });

  const customFields = useQuery({
    queryKey: ['credentials', 'template-fields'],
    queryFn: () => api<{ data: UserCustomFieldOption[] }>('/credentials/templates/fields'),
  });

  useEffect(() => {
    if (query.data?.layout) setLayout(query.data.layout);
  }, [query.data]);

  useEffect(() => {
    if (!pickerOpen) return;
    const handle = window.setTimeout(() => {
      void (async () => {
        setPickerLoading(true);
        try {
          const params = new URLSearchParams({ search: pickerSearch, page: '1', perPage: '24', type: 'image' });
          const data = await api<{ data: MediaHit[] }>(`/media/files?${params}`);
          setPickerResults((data.data ?? []).filter((file) => file.mimeType.startsWith('image/')));
        } catch (error) {
          setPickerResults([]);
          toast(roleActionErrorToast(error, 'No se pudieron cargar las imágenes'));
        } finally {
          setPickerLoading(false);
        }
      })();
    }, 300);
    return () => window.clearTimeout(handle);
  }, [pickerOpen, pickerSearch, pickerNonce, toast]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pickerOpen]);

  const save = useMutation({
    mutationFn: (next: CredentialLayout) => api(`/credentials/templates/${id}`, { method: 'PATCH', body: { layout: next } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credentials', 'templates', id] });
      toast({ title: 'Plantilla guardada', variant: 'success' });
    },
    onError: (error) => toast(roleActionErrorToast(error, 'Error al guardar')),
  });

  const publish = useMutation({
    mutationFn: (next: CredentialLayout) =>
      api(`/credentials/templates/${id}/publish`, { method: 'POST', body: { layout: next } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['credentials', 'templates', id] });
      toast({ title: 'Plantilla publicada', variant: 'success' });
    },
    onError: (error) => toast(roleActionErrorToast(error, 'No se pudo publicar la plantilla')),
  });

  const selected = layout?.elements.find((el) => el.id === selectedId) ?? null;
  const scale = layout ? mmScale(layout, zoom) : 1;
  const backgroundUrl = layout?.canvas.background.media_id
    ? `/api/media/files/${layout.canvas.background.media_id}/content`
    : null;

  function patchElement(elementId: string, patch: Partial<CredentialElement>) {
    setLayout((prev) =>
      prev
        ? { ...prev, elements: prev.elements.map((el) => (el.id === elementId ? { ...el, ...patch } : el)) }
        : prev,
    );
  }

  function addElement(type: 'field' | 'text' | 'qr') {
    const elementId = `el-${Date.now()}`;
    setLayout((prev) => {
      if (!prev) return prev;
      const el: CredentialElement = {
        id: elementId,
        type,
        x: 20,
        y: 20 + prev.elements.length * 15,
        width: type === 'qr' ? 30 : 80,
        height: type === 'qr' ? 30 : 12,
        style: { fontSize: 14, align: 'left' },
        ...(type === 'field' ? { field: 'recipient_name' } : {}),
        ...(type === 'text' ? { content: 'Texto' } : {}),
      };
      return { ...prev, elements: [...prev.elements, el] };
    });
    setSelectedId(elementId);
  }

  function duplicateSelected() {
    if (!selected) return;
    const copy: CredentialElement = { ...selected, style: selected.style ? { ...selected.style } : undefined, id: `el-${Date.now()}`, x: selected.x + 10, y: selected.y + 10 };
    setLayout((prev) => (prev ? { ...prev, elements: [...prev.elements, copy] } : prev));
    setSelectedId(copy.id);
  }

  function removeSelected() {
    if (!selectedId) return;
    setLayout((prev) => (prev ? { ...prev, elements: prev.elements.filter((el) => el.id !== selectedId) } : prev));
    setSelectedId(null);
  }

  function alignSelected(edge: 'left' | 'center' | 'right' | 'bottom') {
    if (!layout || !selected) return;
    const patch: Partial<CredentialElement> = {};
    if (edge === 'left') patch.x = 0;
    if (edge === 'center') patch.x = Math.max(0, Math.round((layout.canvas.width - selected.width) / 2));
    if (edge === 'right') patch.x = Math.max(0, layout.canvas.width - selected.width);
    if (edge === 'bottom') patch.y = Math.max(0, layout.canvas.height - selected.height);
    patchElement(selected.id, patch);
  }

  function setBackground(patch: Partial<CredentialLayout['canvas']['background']>) {
    setLayout((prev) =>
      prev
        ? { ...prev, canvas: { ...prev.canvas, background: { ...prev.canvas.background, ...patch } } }
        : prev,
    );
  }

  function openPicker() {
    setPickerSearch('');
    setPickerOpen(true);
    setPickerNonce((n) => n + 1);
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>, el: CredentialElement) {
    if (!layout) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedId(el.id);
    dragRef.current = {
      id: el.id,
      startX: event.clientX,
      startY: event.clientY,
      origX: el.x,
      origY: el.y,
      scale: mmScale(layout, zoom),
    };
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    const x = Math.max(0, Math.round(drag.origX + (event.clientX - drag.startX) / drag.scale));
    const y = Math.max(0, Math.round(drag.origY + (event.clientY - drag.startY) / drag.scale));
    patchElement(drag.id, { x, y });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  const busy = save.isPending || publish.isPending;
  const fieldOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [
      ...FIELD_BINDINGS,
      ...(customFields.data?.data ?? [])
        .filter((field) => field.slug)
        .map((field) => ({ value: `custom:${field.slug}`, label: field.name })),
    ];
    if (selected?.field && !options.some((field) => field.value === selected.field)) {
      options.push({
        value: selected.field,
        label: selected.field === 'custom' ? 'Campo personalizado' : selected.field,
      });
    }
    return options;
  }, [customFields.data, selected?.field]);
  const fieldLabels = useMemo(() => new Map(fieldOptions.map((field) => [field.value, field.label])), [fieldOptions]);

  const hex = /^#[0-9a-fA-F]{6}$/.test(layout?.canvas.background.color || '')
    ? layout!.canvas.background.color
    : '#ffffff';
  const sizeLabel = layout
    ? `${layout.canvas.width === 297 && layout.canvas.height === 210 ? 'A4 Landscape • ' : layout.canvas.width === 210 && layout.canvas.height === 297 ? 'A4 Portrait • ' : ''}${layout.canvas.width} × ${layout.canvas.height} mm`
    : '';

  return (
    <div className="lms-credential-editor space-y-6 font-sans md:-mt-2">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <HubBackLink to={`/credenciales/plantillas/${id ?? ''}`} label="Volver a plantillas" />
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-dash-primary md:text-3xl">Editor — {query.data?.name ?? 'Plantilla'}</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">Diseño visual de la plantilla</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 md:pl-14">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-dash-secondary px-5 py-2.5 text-sm font-medium text-dash-secondary transition-colors hover:bg-brand-50 disabled:opacity-50"
            disabled={!layout || busy}
            onClick={() => layout && save.mutate(layout)}
          >
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-50"
            disabled={!layout || busy}
            onClick={() => layout && publish.mutate(layout)}
          >
            {publish.isPending ? 'Publicando…' : 'Publicar'}
          </button>
        </div>
      </header>
      {query.error && (
        <p className="text-sm text-red-600" role="alert">
          {(query.error as Error).message}
        </p>
      )}

      {query.isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader label="Cargando editor…" />
        </div>
      ) : !layout ? (
        <p className="p-8 text-sm text-dash-on-surface-variant">No se pudo cargar el diseño de la plantilla.</p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
          <aside className="flex flex-col gap-6 lg:col-span-3">
            <section className="space-y-3.5 rounded-xl border border-[#e2e2e5] bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">Herramientas</h3>
              <div className="grid grid-cols-3 gap-2.5">
                <ToolButton icon="add_box" label="+ Campo" active={selected?.type === 'field'} onClick={() => addElement('field')} />
                <ToolButton icon="text_fields" label="+ Texto" active={selected?.type === 'text'} onClick={() => addElement('text')} />
                <ToolButton icon="qr_code_2" label="+ QR" active={selected?.type === 'qr'} onClick={() => addElement('qr')} />
              </div>
            </section>
            <section className="space-y-4 rounded-xl border border-[#e2e2e5] bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">Fondo</h3>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600" htmlFor="credential-bg-hex">
                  Color base
                </label>
                <div className="flex items-center gap-2">
                  <label className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-gray-200 shadow-sm" style={{ backgroundColor: hex }}>
                    <input
                      type="color"
                      className="absolute inset-0 cursor-pointer opacity-0"
                      value={hex}
                      aria-label="Color base"
                      onChange={(event) => setBackground({ color: event.target.value })}
                    />
                  </label>
                  <input
                    id="credential-bg-hex"
                    type="text"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-[#000256] focus:ring-1 focus:ring-[#000256]"
                    value={(layout.canvas.background.color || '#ffffff').toUpperCase()}
                    onChange={(event) => {
                      let next = event.target.value.trim().toUpperCase();
                      if (!next.startsWith('#')) next = `#${next}`;
                      if (/^#[0-9A-F]{0,6}$/.test(next)) setBackground({ color: next });
                    }}
                  />
                </div>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">Imagen de fondo</p>
                <button
                  type="button"
                  className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                  onClick={openPicker}
                >
                  <span className="material-symbols-outlined text-[18px] text-gray-500" aria-hidden="true">image</span>
                  {backgroundUrl ? 'Cambiar imagen' : 'Elegir imagen'}
                </button>
                {backgroundUrl && (
                  <button type="button" className="mt-2 text-xs font-medium text-red-600 hover:underline" onClick={() => setBackground({ media_id: null })}>
                    Quitar imagen
                  </button>
                )}
              </div>
            </section>
            <section className="space-y-3 rounded-xl border border-[#e2e2e5] bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">Elementos (Capas)</h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                  {layout.elements.length} {layout.elements.length === 1 ? 'capa' : 'capas'}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                {layout.elements.map((el) => {
                  const active = selectedId === el.id;
                  return (
                    <button
                      key={el.id}
                      type="button"
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors ${
                        active
                          ? 'border border-blue-200 bg-blue-50/70 font-semibold text-[#00227B]'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedId(el.id)}
                    >
                      <span className={`material-symbols-outlined text-[18px] ${active ? 'text-[#00227B]' : 'text-gray-400'}`} aria-hidden="true">
                        drag_indicator
                      </span>
                      <span className={`material-symbols-outlined text-[16px] ${active ? 'text-[#00227B]' : 'text-gray-500'}`} aria-hidden="true">
                        {layerIcon(el)}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{layerName(el, fieldLabels)}</span>
                      {active && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00227B]" />}
                    </button>
                  );
                })}
                {layout.elements.length === 0 && (
                  <p className="py-4 text-center text-xs text-gray-500">Añade elementos con las herramientas.</p>
                )}
              </div>
            </section>
          </aside>

          <div className="flex flex-col items-center lg:col-span-6">
          <section className="relative flex min-h-[480px] w-full items-center justify-center overflow-auto rounded-2xl border border-[#e2e2e5] bg-gray-100/80 p-6 shadow-inner sm:p-8">
            <p className="absolute left-4 top-3 text-xs font-semibold tracking-wide text-gray-400">{sizeLabel}</p>
            <div
              className="lms-credential-editor__canvas relative shrink-0 overflow-hidden rounded-xl border border-[#e2e2e5] bg-white shadow-xl"
              style={{
                width: layout.canvas.width * scale,
                height: layout.canvas.height * scale,
                backgroundColor: layout.canvas.background.color || '#ffffff',
                backgroundImage: backgroundUrl ? `url('${backgroundUrl}')` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              onClick={() => setSelectedId(null)}
            >
              {layout.elements.map((el) => (
                <div
                  key={el.id}
                  className={`absolute cursor-move select-none border px-1 ${
                    selectedId === el.id
                      ? 'z-10 border-2 border-[#00227B] bg-blue-500/10'
                      : 'border-transparent hover:border-[#00227B]/40'
                  }`}
                  style={elementStyle(el, scale)}
                  onPointerDown={(event) => onPointerDown(event, el)}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedId(el.id);
                  }}
                >
                  {el.type === 'qr' ? (
                    <div className="flex h-full w-full flex-col items-center justify-center border border-dash-outline-variant/50 bg-dash-on-surface/5">
                      <span
                        className="material-symbols-outlined text-dash-on-surface-variant"
                        style={{ fontSize: Math.min(36, el.height * scale * 0.45) }}
                        aria-hidden="true"
                      >
                        qr_code_2
                      </span>
                      {selectedId === el.id && (
                        <span className="absolute -top-6 right-0 whitespace-nowrap rounded bg-[#00227B] px-2 py-0.5 text-[10px] font-semibold text-white">
                          QR • {el.width}×{el.height} mm
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="w-full overflow-hidden text-ellipsis whitespace-nowrap">{displayLabel(el, fieldLabels)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
            <div className="mt-4 flex items-center gap-2 rounded-full border border-[#e2e2e5] bg-white px-4 py-2 text-sm text-gray-600 shadow-sm">
              <button type="button" className="flex items-center hover:text-gray-900" aria-label="Alejar" onClick={() => setZoom((value) => Math.max(0.5, Math.round((value - 0.25) * 100) / 100))}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">zoom_out</span>
              </button>
              <span className="px-2 text-xs font-semibold text-gray-800">{Math.round(zoom * 100)}%</span>
              <button type="button" className="flex items-center hover:text-gray-900" aria-label="Acercar" onClick={() => setZoom((value) => Math.min(2, Math.round((value + 0.25) * 100) / 100))}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">zoom_in</span>
              </button>
              <span className="px-1 text-gray-300">•</span>
              <button type="button" className="flex items-center hover:text-gray-900" aria-label="Ajustar" onClick={() => setZoom(1)}>
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">fit_screen</span>
              </button>
            </div>
          </div>

          <aside className="lg:col-span-3">
            <section className="space-y-5 rounded-xl border border-[#e2e2e5] bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">Propiedades</h3>
                {selected && (
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-[#00227B]">
                    {typeBadge(selected.type)}
                  </span>
                )}
              </div>
              {selected ? (
                <>
                  {selected.type === 'field' && (
                    <label className="block text-sm">
                      <span className="text-xs font-semibold text-gray-600">Campo</span>
                      <select
                        className={`${INPUT} mt-1.5`}
                        value={selected.field ?? 'recipient_name'}
                        onChange={(event) => patchElement(selected.id, { field: event.target.value })}
                      >
                        {fieldOptions.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {selected.type === 'text' && (
                    <label className="block text-sm">
                      <span className="text-xs font-semibold text-gray-600">Texto</span>
                      <input
                        type="text"
                        className={`${INPUT} mt-1.5`}
                        value={selected.content ?? ''}
                        onChange={(event) => patchElement(selected.id, { content: event.target.value })}
                      />
                    </label>
                  )}
                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Geometría (mm)</p>
                    <div className="grid grid-cols-2 gap-3">
                      {(['x', 'y', 'width', 'height'] as const).map((key) => (
                        <label key={key} className="space-y-1.5">
                          <span className="block text-xs font-semibold text-gray-600">
                            {key === 'x' ? 'X (mm)' : key === 'y' ? 'Y (mm)' : key === 'width' ? 'Ancho' : 'Alto'}
                          </span>
                          <span className="relative flex items-center">
                            <input
                              type="number"
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-8 text-sm text-gray-800 outline-none focus:border-[#000256] focus:ring-1 focus:ring-[#000256]"
                              step={1}
                              value={selected[key]}
                              onChange={(event) => {
                                const n = Number(event.target.value);
                                if (Number.isFinite(n)) patchElement(selected.id, { [key]: Math.round(n) });
                              }}
                            />
                            <span className="pointer-events-none absolute right-2.5 text-xs text-gray-400">mm</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-gray-100 pt-2">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Alineación</p>
                    <div className="flex items-center gap-1.5">
                      {(
                        [
                          ['left', 'Alinear a la izquierda', 'align_horizontal_left'],
                          ['center', 'Alinear al centro', 'align_horizontal_center'],
                          ['right', 'Alinear a la derecha', 'align_horizontal_right'],
                          ['bottom', 'Alinear abajo', 'align_vertical_bottom'],
                        ] as const
                      ).map(([edge, label, icon]) => (
                        <button
                          key={edge}
                          type="button"
                          aria-label={label}
                          className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white py-1.5 text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                          onClick={() => alignSelected(edge)}
                        >
                          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">{icon}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2.5 border-t border-gray-100 pt-4">
                    <button
                      type="button"
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                      onClick={duplicateSelected}
                    >
                      <span className="material-symbols-outlined text-[18px]" aria-hidden="true">content_copy</span>
                      Duplicar
                    </button>
                    <button
                      type="button"
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 shadow-sm transition-colors hover:bg-red-50"
                      onClick={removeSelected}
                    >
                      <span className="material-symbols-outlined text-[18px]" aria-hidden="true">delete</span>
                      Eliminar
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">Selecciona un elemento del lienzo.</p>
              )}
            </section>
          </aside>
        </div>
      )}

      {pickerOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-dash-on-surface/40 p-4 backdrop-blur-sm"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-dash-outline-variant bg-white shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="credential-bg-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-dash-outline-variant px-6 py-4">
              <h3 id="credential-bg-title" className="text-lg font-semibold text-dash-on-surface">
                Imagen de fondo
              </h3>
              <button type="button" className="rounded-full p-2 text-dash-on-surface-variant hover:bg-secondary-100" onClick={() => setPickerOpen(false)} aria-label="Cerrar">
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>
            <div className="border-b border-dash-outline-variant p-4">
              <input
                type="search"
                value={pickerSearch}
                onChange={(event) => setPickerSearch(event.target.value)}
                placeholder="Buscar en biblioteca multimedia..."
                className={INPUT}
              />
            </div>
            <div className="relative min-h-[14rem] flex-1 overflow-y-auto p-4">
              {pickerLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-sm text-dash-on-surface-variant">Cargando…</div>
              )}
              {pickerResults.length > 0 && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {pickerResults.map((file) => (
                    <button
                      key={file.id}
                      type="button"
                      className="rounded-xl border border-dash-outline-variant p-3 text-left transition-colors hover:border-dash-secondary hover:bg-brand-50/50"
                      onClick={() => {
                        setLayout((prev) =>
                          prev
                            ? {
                                ...prev,
                                canvas: { ...prev.canvas, background: { ...prev.canvas.background, media_id: file.id } },
                              }
                            : prev,
                        );
                        setPickerOpen(false);
                      }}
                    >
                      <p className="text-sm font-medium text-dash-on-surface">{file.title}</p>
                      <p className="text-xs text-dash-on-surface-variant">{file.mimeType}</p>
                    </button>
                  ))}
                </div>
              )}
              {!pickerLoading && pickerResults.length === 0 && (
                <p className="py-8 text-center text-sm text-dash-on-surface-variant">No se encontraron imágenes.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolButton({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`flex flex-col items-center justify-center space-y-1.5 rounded-lg px-2 py-3 text-xs font-semibold shadow-sm transition-colors ${
        active ? 'bg-[#00227B] text-white' : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <span className={`material-symbols-outlined text-[20px] ${active ? '' : 'text-[#00227B]'}`} aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}
